import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Download } from "lucide-react";
import { LogoMark, LogoWord } from "@/components/Logo";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-link-home" },
  { to: "/features", label: "Features", testid: "nav-link-features" },
  { to: "/about", label: "About", testid: "nav-link-about" },
  { to: "/faqs", label: "FAQs", testid: "nav-link-faqs" },
];

export const DOWNLOAD_URL = "https://onelink.to/bqybyq";

export default function Nav() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-[#0B0B0D]/80 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2.5 group">
          <LogoMark />
          <LogoWord className="text-xl" />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                  isActive ? "text-primary bg-primary/10" : "text-slate-300 hover:text-white hover:bg-white/5"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={DOWNLOAD_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-download-button"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-[#0B0B0D] transition-transform duration-200 hover:scale-[1.04] active:scale-95"
          >
            <Download size={15} strokeWidth={2.5} />
            Get the App
          </a>
          <button
            data-testid="nav-mobile-menu-button"
            className="md:hidden text-slate-200 p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="md:hidden overflow-hidden border-t border-white/10 bg-[#0B0B0D]/95"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => setOpen(false)}
                  className={`px-3 py-3 rounded-lg text-base font-medium ${
                    location.pathname === l.to ? "text-primary bg-primary/10" : "text-slate-200"
                  }`}
                >
                  {l.label}
                </Link>
              ))}
              <a
                href={DOWNLOAD_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-download-button-mobile"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-semibold text-[#0B0B0D]"
              >
                <Download size={15} strokeWidth={2.5} />
                Get the App
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
